import { inject, Injectable } from '@angular/core';
import { AllTopbarFeatures, TopbarJson } from './symbols';
import { AwareAuthService } from '@appbolaget/aware-auth';
import { BehaviorSubject, combineLatest, debounceTime, defer, filter, iif, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { Attribute, Client } from '@appbolaget/aware-model';
import { TopbarFeature } from './TopbarFeature';
import { DialogService } from '@viewservices';
import { HelpMainDialogComponent } from '@modules/help/main-dialog/main-dialog.component';
import { MediaService } from '@modules/media/services/media.service';
import { MessagesService } from '@pages/messages/messages.service';
import { Config } from '@services';
import { Router } from '@angular/router';
import { IMessagesConfig } from '@pages/messages/messages.config';
import { MESSAGES_ROUTE } from '@pages/messages/symbols';
import { Store } from '@ngxs/store';
import { NotificationState } from '@modules/notification/notification.state';
import { UnitState } from 'app/state/unit.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientService } from '@pages/clients/clients.service';

@Injectable({ providedIn: 'root' })
export class TopbarService {
    #awareAuth = inject(AwareAuthService);
    #dialogService = inject(DialogService);
    #mediaService = inject(MediaService);
    #messagesService = inject(MessagesService);
    #clientService = inject(ClientService);
    #config = inject(Config);
    #router = inject(Router);
    #store = inject(Store);
    availableFeaturesObject = AllTopbarFeatures;
    availableFeatures = Object.values(AllTopbarFeatures);
    toggledFeatures$ = new BehaviorSubject<TopbarJson>({});
    togglePinnedFeature$ = new Subject<TopbarFeature>();

    applicableFeatures$: Observable<TopbarFeature[]> = this.#store
        .select(UnitState.activeUnit)
        .pipe(map(() => this.#parseFeatures(this.availableFeatures)));

    currentFeatures$: Observable<TopbarFeature[]> = combineLatest([
        combineLatest([
            this.#awareAuth.clientFiltered$.pipe(
                filter((client) => client instanceof Client),
                map((client) => client.attr('topbar_features', 'json')),
                map((features) => {
                    if (!features) {
                        return {};
                    }
                    try {
                        return JSON.parse(features) as TopbarJson;
                    } catch {
                        return {};
                    }
                }),
            ),
            this.toggledFeatures$,
        ]).pipe(
            map(([features, toggledFeatures]) => {
                const merged = { ...features, ...toggledFeatures };
                return this.availableFeatures.filter((feature) => {
                    if (merged.hasOwnProperty(feature.key)) {
                        // If the feature is in the merged object, use its value
                        return merged[feature.key];
                    }
                    // Otherwise, use default pinned value
                    return feature.pinned;
                });
            }),
        ),
        this.#store.select(UnitState.activeUnit),
    ]).pipe(map(([features]) => this.#parseFeatures(features)));

    mergedApplicableFeatures$: Observable<TopbarFeature[]> = combineLatest([this.applicableFeatures$, this.currentFeatures$]).pipe(
        map(([features, currentFeatures]) => {
            return features.map((feature) => {
                feature.pinned = currentFeatures.some((currentFeature) => currentFeature.key === feature.key);
                return feature;
            });
        }),
    );
    mergedBadgeSourcesForHiddenFeatures$: Observable<number> = this.mergedApplicableFeatures$.pipe(
        map((features) =>
            features.filter(
                (feature) =>
                    !feature.pinned && feature.badge?.enabled && feature.badge.source && feature.badge.source instanceof Observable,
            ),
        ),
        switchMap((features) =>
            iif(
                () => features.length === 0,
                // If no hidden features, return an observable that emits 0
                of(0),
                defer(() =>
                    combineLatest(features.map((feature) => feature.badge.source)).pipe(
                        map((badgeCounts) => badgeCounts.reduce((acc, count) => acc + count, 0)),
                    ),
                ),
            ),
        ),
        map((number) => (isNaN(number) ? 0 : number)),
    );

    badgeSources: { [key: string]: Observable<number> } = {
        messagesAction: this.#messagesService.unreadThreads$,
        systemnoticesAction: this.#store
            .select(NotificationState.notificationsByType('systemnotice'))
            .pipe(map((notifications) => notifications?.length)),
    };

    constructor() {
        this.togglePinnedFeature$
            .pipe(
                takeUntilDestroyed(),
                tap((feature) => this.togglePinnedFeature(feature)),
                switchMap(() => this.mergedApplicableFeatures$),
                debounceTime(300),
                switchMap((features) => this.#persistToggledFeatures(features)),
            )
            .subscribe();
    }

    featureClicked(feature: TopbarFeature, event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();

        if (feature.action && typeof this[feature.action] === 'function') {
            this[feature.action](event);
        } else {
            console.warn(`Action "${feature.action}" is not defined in TopbarService.`);
        }
    }

    togglePinnedFeature(feature: TopbarFeature): void {
        const toggledFeatures = this.toggledFeatures$.getValue();
        if (toggledFeatures.hasOwnProperty(feature.key)) {
            toggledFeatures[feature.key] = !toggledFeatures[feature.key];
        } else {
            toggledFeatures[feature.key] = !feature.pinned;
        }

        // If the toggled state matches the pinned state, remove it from toggledFeatures
        if (toggledFeatures[feature.key] === feature.pinned) {
            delete toggledFeatures[feature.key];
        }

        this.toggledFeatures$.next(toggledFeatures);
    }

    helpAction(event: MouseEvent): void {
        const right = window.innerWidth - event.pageX;

        this.#dialogService.open(HelpMainDialogComponent, {
            height: '500px',
            position: {
                top: '65px',
                right: `${right}px`,
            },
        });
    }

    medialibraryAction(_event: MouseEvent): void {
        this.#mediaService.openDialog();
    }

    async messagesAction(event: MouseEvent): Promise<void> {
        const { MessagesThreadListDialogComponent } =
            await import('@pages/messages/components/thread-list-dialog/thread-list-dialog.component');
        const messagesTopbarConfig = this.#config.get<IMessagesConfig['topbar']>('messages.topbar');
        if (messagesTopbarConfig.dialog) {
            const right = window.innerWidth - event.pageX;

            this.#dialogService.open(MessagesThreadListDialogComponent, {
                width: '90%',
                height: '90%',
                maxWidth: '350px',
                maxHeight: '500px',
                position: {
                    top: '65px',
                    right: `${right}px`,
                },
            });
        } else {
            this.#router.navigate([MESSAGES_ROUTE]);
        }
    }

    systemnoticesAction(event: MouseEvent): void {
        const right = window.innerWidth - event.pageX;

        this.#dialogService.open(HelpMainDialogComponent, {
            height: '500px',
            position: {
                top: '65px',
                right: `${right}px`,
            },
            data: {
                type: 'systemnotice',
                hideSearch: true,
                showDate: true,
                showNotificationBadge: true,
                title: 'Systemmeddelanden',
                params: {
                    sort: JSON.stringify({ publish_at: 'desc' }),
                },
            },
        });
    }

    #parseFeatures(features: TopbarFeature[]): TopbarFeature[] {
        return features
            .filter((feature) => {
                if (feature.requiredConfig && !this.#config.get(feature.requiredConfig)) {
                    return false;
                }
                if (feature.reverseRequiredConfig && this.#config.get(feature.reverseRequiredConfig)) {
                    return false;
                }

                return true;
            })
            .map((feature) => {
                if (feature.badge?.enabled) {
                    feature.badge.source = this.badgeSources[feature.action] || feature.badge.source;
                }
                return feature;
            });
    }

    #persistToggledFeatures(features: TopbarFeature[]): Observable<void> {
        const attr = features.reduce((acc, feature) => {
            acc[feature.key] = feature.pinned;
            return acc;
        }, {} as TopbarJson);

        return this.#clientService.saveAttributes(this.#awareAuth.client, [new Attribute({ key: 'topbar_features', json: attr })]);
    }
}
